import { exhaustiveCheck } from '@sixfold/typed-primitives';

import {
  CompanySignupVisibilitySourceStatus,
  TourVisibilitySourceType,
  UnifiedCompanySignupEventType,
} from '../lib/graphql';

export const SUBCARRIERS_SOURCE_LABEL = 'Subcarriers';

export function getSubcarrierSourceLabel(type: TourVisibilitySourceType): string {
  return `Subcarrier: ${getVisibilitySourceLabel(type)}`;
}

export const VISIBILITY_SOURCES_COLUMN_HINT =
  'Every visibility source that the Source Company has on the platform, including deactivated ones.';

export function getVisibilitySourceLabel(type: TourVisibilitySourceType): string {
  switch (type) {
    case TourVisibilitySourceType.CARRIER_API:
      return 'Legacy API (Sixfold)';
    case TourVisibilitySourceType.FMS:
      return 'FMS';
    case TourVisibilitySourceType.IOT_TRACKER:
      return 'IoT tracker';
    case TourVisibilitySourceType.MOBILE:
      return 'Visibility mobile';
    case TourVisibilitySourceType.PLATFORM_API:
      return 'Transporeon (forwarded)';
    case TourVisibilitySourceType.PLATFORM_RTV_API:
      return 'Visibility API';
    case TourVisibilitySourceType.TTC:
      return 'TTC';
    case TourVisibilitySourceType.TTC_MOBILE:
      return 'Legacy mobile';
    default:
      return exhaustiveCheck(type);
  }
}

/** Short explanation for sources whose label alone is not self-explanatory. `null` means the label is clear enough. */
export function getVisibilitySourceDescription(type: TourVisibilitySourceType): string | null {
  switch (type) {
    case TourVisibilitySourceType.CARRIER_API:
      return 'Covers: Shipment Status Update API and Telematics API.';
    case TourVisibilitySourceType.PLATFORM_API:
      return 'Covers: MOM, TSM, Trucker App, and EDI.';
    case TourVisibilitySourceType.TTC_MOBILE:
      return 'Legacy Drive App (10-4).';
    case TourVisibilitySourceType.MOBILE:
      return 'Freight Visibility Drive App';
    case TourVisibilitySourceType.FMS:
    case TourVisibilitySourceType.IOT_TRACKER:
    case TourVisibilitySourceType.PLATFORM_RTV_API:
    case TourVisibilitySourceType.TTC:
      return null;
    default:
      return exhaustiveCheck(type);
  }
}

export function getVisibilitySourceStatusExplanation(status: CompanySignupVisibilitySourceStatus): string {
  switch (status) {
    case CompanySignupVisibilitySourceStatus.ACTIVE:
      return 'Started sending tracking data.';
    case CompanySignupVisibilitySourceStatus.IDENTIFIED:
      return 'Set up, but no tracking data yet.';
    case CompanySignupVisibilitySourceStatus.DEACTIVATED:
      return 'Disconnected, or no tracking data for 30 days.';
    default:
      return exhaustiveCheck(status);
  }
}

export function getVisibilitySourceStatusLabel(status: CompanySignupVisibilitySourceStatus): string {
  switch (status) {
    case CompanySignupVisibilitySourceStatus.ACTIVE:
      return 'Active';
    case CompanySignupVisibilitySourceStatus.IDENTIFIED:
      return 'Identified';
    case CompanySignupVisibilitySourceStatus.DEACTIVATED:
      return 'Deactivated';
    default:
      return exhaustiveCheck(status);
  }
}

const STATUS_ORDER: Record<CompanySignupVisibilitySourceStatus, number> = {
  [CompanySignupVisibilitySourceStatus.ACTIVE]: 0,
  [CompanySignupVisibilitySourceStatus.IDENTIFIED]: 1,
  [CompanySignupVisibilitySourceStatus.DEACTIVATED]: 2,
};

function getStatusEventType(status: CompanySignupVisibilitySourceStatus): UnifiedCompanySignupEventType {
  switch (status) {
    case CompanySignupVisibilitySourceStatus.ACTIVE:
      return UnifiedCompanySignupEventType.VISIBILITY_SOURCE_ACTIVATED;
    case CompanySignupVisibilitySourceStatus.IDENTIFIED:
      return UnifiedCompanySignupEventType.VISIBILITY_SOURCE_IDENTIFIED;
    case CompanySignupVisibilitySourceStatus.DEACTIVATED:
      return UnifiedCompanySignupEventType.VISIBILITY_SOURCE_DEACTIVATED;
    default:
      return exhaustiveCheck(status);
  }
}

export function getVisibilitySourceStatusSince(
  source: { type: TourVisibilitySourceType; status: CompanySignupVisibilitySourceStatus },
  events: {
    type: UnifiedCompanySignupEventType;
    eventTime: string;
    visibilitySource: TourVisibilitySourceType | null;
  }[],
): string | null {
  const statusEventType = getStatusEventType(source.status);
  const matchingTimes = events
    .filter(({ type, visibilitySource }) => type === statusEventType && visibilitySource === source.type)
    .map(({ eventTime }) => eventTime);

  if (matchingTimes.length === 0) {
    return null;
  }
  return matchingTimes.reduce((latest, time) => (new Date(time) > new Date(latest) ? time : latest));
}

export function sortVisibilitySourcesByStatus<T extends { status: CompanySignupVisibilitySourceStatus }>(
  sources: T[],
): T[] {
  return [...sources].sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status]);
}

export function getBestVisibilitySourceStatus(
  sources: { status: CompanySignupVisibilitySourceStatus }[],
): CompanySignupVisibilitySourceStatus | null {
  const [best] = sortVisibilitySourcesByStatus(sources);
  return best?.status ?? null;
}
